import { HttpClient } from '@angular/common/http';
import { Injectable, computed, inject, signal } from '@angular/core';
import { Subject, firstValueFrom } from 'rxjs';
import { ErrorsService } from 'src/app/core/errors/errors-service/errors.service';
import { BrowserPlatformService } from 'src/app/core/services/browser-platform/browser-platform.service';
import type { SiteUser } from './user.service';

/**
 * The site user as ANGULAR state - the successor of the user-state half of
 * Content/scripts/Yeshiva/UserBar.js (settings/whenReadyPromise/isUserConnected and the getters).
 *
 * Phase 1 (shadow mode): this service resolves the user on its own, exactly like UserBar does -
 * the login cookie decides whether /Users/UserName/ is asked at all - but nothing reads it yet
 * except UserService's shadow comparison, which logs every disagreement with UserBar. Once the
 * comparison stays quiet, UserService switches its accessors to these signals and the UserBar
 * script leaves the Angular pages.
 *
 * Anonymous = user() with Id 0 (or null before the first resolve). Nothing here knows the cms
 * address: the cms links are values the server revealed on a real login (see the cardinal rule).
 */
@Injectable({ providedIn: 'root' })
export class UserStateService {
    private readonly _http = inject(HttpClient);
    private readonly _platform = inject(BrowserPlatformService);
    private readonly _errors = inject(ErrorsService);

    /** the site login cookie's name as it appears in document.cookie (UserBar's cookieLoginlname) */
    static readonly LOGIN_COOKIE = 'zpbwxsSyX4h0fGZQICW5pQ%3d%3d';
    static readonly USER_URL = '/Users/UserName/';

    private readonly _user = signal<SiteUser | null>(null);
    /** the current user json; null until the first resolve, Id 0 when anonymous */
    readonly user = this._user.asReadonly();
    /** true once the first resolve settled (with or without a server call) */
    readonly resolved = signal(false);
    /** every apply()/clear(): the user json, or Id 0 for anonymous (UserService's getUser() stream) */
    readonly changes$ = new Subject<SiteUser>();
    readonly loggedIn = computed(() => {
        const user = this._user();
        return !!user && user.Id > 0;
    });
    readonly isInternal = computed(() => this.flag('isInternal'));

    private _readyPromise: Promise<boolean> | null = null;
    //the memoized answer was the cookie-less short-circuit (no server call). A forceCheck caller
    //must not be served that guess: a nihul login's site cookie is SHORT and only a real
    ///Users/UserName/ call re-issues it from the sliding cms ticket (UserBar's own rule)
    private _resolvedWithoutCheck = false;

    /** a nihul flag or any other boolean field of the user json (false while anonymous) */
    flag(field: string): boolean {
        return this._field(field) === true;
    }

    /** a server-revealed link of the user json (cmsBaseLink, cmsApiLink, cmsDisconnectLink, cmsContentLink, repliesLink) */
    link(field: string): string {
        const value = this._field(field);
        return typeof value === 'string' ? value : '';
    }

    /** a numeric field of the user json (the rabbi ids: AskHebRavId, AskEngRavId, MidrashRavId) */
    number(field: string): number | undefined {
        const value = this._field(field);
        return typeof value === 'number' ? value : undefined;
    }

    private _field(field: string): unknown {
        const user = this._user();
        return user && user.Id > 0 ? user[field] : undefined;
    }

    /** UserBar's isUserConnected: the login cookie is present, or the server said internal */
    isConnected(): boolean {
        return this.hasLoginCookie() || this.isInternal();
    }

    hasLoginCookie(): boolean {
        if (!this._platform.isBrowser) {
            return false;
        }
        const cookies = this._platform.document.cookie || '';
        return cookies.split(';').some((part) => part.trim().indexOf(UserStateService.LOGIN_COOKIE + '=') === 0);
    }

    /**
     * Resolve the user once per page load: with the login cookie (or forceCheck) the server is
     * asked, without it the answer is "anonymous" with zero requests. Resolves to "logged in".
     */
    ready(forceCheck = false): Promise<boolean> {
        if (!this._platform.isBrowser) {
            return Promise.resolve(false);
        }
        if (!this._readyPromise || (forceCheck && this._resolvedWithoutCheck)) {
            this._resolvedWithoutCheck = false;
            this._readyPromise = this._resolve(forceCheck);
        }
        return this._readyPromise;
    }

    /** Apply a /Users/UserName-shaped json (a login, the nihul revive, a transfer from the cms). */
    apply(data: Record<string, unknown> | null | undefined): void {
        const user = this._asUser(data);
        this._user.set(user);
        this.resolved.set(true);
        if (user.Id > 0) {
            this._persistCmsLinks(user);
        }
        this.changes$.next(user);
    }

    /** The user logged out: anonymous from now on (the cookie itself is the server's to clear). */
    clear(): void {
        const user = { Id: 0 };
        this._user.set(user);
        this.resolved.set(true);
        this.changes$.next(user);
    }

    /** Re-read the user json from the server (a login just set the cookie, a cms transfer arrived). */
    refetch(): Promise<boolean> {
        this._resolvedWithoutCheck = false;
        this._readyPromise = this._fetch();
        return this._readyPromise;
    }

    private async _resolve(forceCheck: boolean): Promise<boolean> {
        if (!forceCheck && !this.hasLoginCookie()) {
            this._resolvedWithoutCheck = true;
            this.clear();
            return false;
        }
        return this._fetch();
    }

    private async _fetch(): Promise<boolean> {
        try {
            //per-user identity json: the server declares no-store, the noCache header keeps our own
            //interceptors from caching it either
            const data = await firstValueFrom(this._http.get<Record<string, unknown>>(UserStateService.USER_URL, { headers: { noCache: 'true' } }));
            this.apply(data);
        } catch (error) {
            //unreachable server: the page behaves as anonymous, a later login applies its own json.
            //Logged, never swallowed - a request failing before it leaves the browser looks the same
            this._errors.log(error);
            this.clear();
        }
        return this.loggedIn();
    }

    private _asUser(data: unknown): SiteUser {
        if (typeof data !== 'object' || data === null) {
            return { Id: 0 };
        }
        const record = data as Record<string, unknown>;
        const id = typeof record['Id'] === 'number' ? record['Id'] : 0;
        return { ...record, Id: id };
    }

    //remember the server-revealed cms links for anonymous-looking loads (an expired login being
    //revived) - the same keys UserBar persists. The values exist ONLY because a real login revealed
    //them; this file never carries any of them as a literal
    private _persistCmsLinks(user: SiteUser): void {
        try {
            const storage = this._platform.localStorage;
            if (!storage) {
                return;
            }
            if (typeof user['cmsApiLink'] === 'string' && user['cmsApiLink']) {
                storage.setItem('cmsApiLink', user['cmsApiLink']);
            }
            if (typeof user['cmsLogonLink'] === 'string' && user['cmsLogonLink']) {
                storage.setItem('cmsLogonUrl', user['cmsLogonLink']);
            }
        } catch {
            //storage blocked
        }
    }
}
